import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useDocumentMeta } from './useDocumentMeta';

/**
 * 404 page for the public site. Replaces the prior `<Navigate to="/">`
 * fallback — silent redirects on unknown URLs are bad for SEO and
 * confuse users who landed via a stale share link.
 *
 * The page sets `<meta name="robots" content="noindex">` so crawlers
 * don't index the 404 itself. We can't return an HTTP 404 from a
 * client-side SPA — Caddy still answers 200 OK — but the visible
 * state and `noindex` are the parts that matter to search engines
 * once Google renders the JS. The meta is removed on unmount so it
 * doesn't bleed onto the next route — leaving `noindex` on `/` after
 * a 404 → home click would silently nuke the homepage from search.
 */
export function NotFound() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: `404 — ${t('site.notfound.title')} · Kit Pod`,
    description: t('site.notfound.body'),
  });
  useNoIndex();
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-5 py-20 text-center sm:px-6">
      <p className="pb-display text-[clamp(72px,12vw,108px)] leading-none text-pb-accent">
        404
      </p>
      <h1 className="pb-display mt-6 text-[clamp(22px,4vw,32px)] leading-tight">
        {t('site.notfound.title')}
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed text-tg-hint">
        {t('site.notfound.body')}
      </p>
      <Link
        to="/"
        className="pb-btn pb-btn-primary pb-focus-ring mt-8 inline-flex px-7 text-[15px]"
      >
        {t('site.notfound.cta')}
      </Link>
    </div>
  );
}

function useNoIndex(): void {
  useEffect(() => {
    let el = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const created = !el;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    const prev = created ? null : el.getAttribute('content');
    el.setAttribute('content', 'noindex, follow');
    return () => {
      // On unmount restore the prior `robots` value (or remove the
      // tag if we created it). Without this, navigating 404 → home
      // would leave `noindex` on `/` and Google would deindex it.
      if (!el) return;
      if (created) {
        el.remove();
      } else if (prev !== null) {
        el.setAttribute('content', prev);
      } else {
        el.removeAttribute('content');
      }
    };
  }, []);
}
